/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { IOverflowSetItemProps, OverflowSet } from '@fluentui/react/lib/OverflowSet';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { HeaderBreadcrumbItem, HeaderBreadcrumbItemProps } from 'src/modules/subHeader/components/HeaderBreadcrumbItem';

const headerTheme = getScopedTheme('GlobalSubHeader');
const { styleComponent } = getStylist('GlobalSubHeader');

const overflowStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    menuIcon: { color: headerTheme.fontColor, fontWeight: 'bold' }
});

const breadcrumbSlashStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { color: headerTheme.fontColor, fontSize: headerTheme.iconSize }
});

export const SubHeaderRoot = styleComponent<IStackProps>(Stack)('SubHeaderRoot', theme => ({
    height: theme.topPanelHeight,
    minHeight: theme.topPanelHeight,
    padding: theme.rootPadding,
    backgroundColor: theme.backgroundColor,
    borderBottom: `1px solid ${theme.headerBorderColor}`
}));

export const onRenderOverflowBreadcrumbs = (items: HeaderBreadcrumbItemProps[], moreOptionsLabel: string) => {
    const breadcrumbItems = items.map<IOverflowSetItemProps>(item => ({
        key: `breadcrumb_${item.text}`,
        name: item.secondaryText ? `${item.text} (${item.secondaryText})` : item.text,
        onClick: item.onClick
    }));

    const onRenderItem = (item: IOverflowSetItemProps) => (
        <Link role="menuitem" onClick={item.onClick}>
            {item.name as string}
        </Link>
    );

    const onRenderOverflowButton = () => (
        <IconButton
            role="menuitem"
            styles={overflowStyles}
            title={moreOptionsLabel}
            ariaLabel={moreOptionsLabel}
            menuIconProps={{ iconName: 'More' }}
            menuProps={{ items: breadcrumbItems }}
        />
    );

    return <OverflowSet onRenderItem={onRenderItem} overflowItems={breadcrumbItems} onRenderOverflowButton={onRenderOverflowButton} />;
};

/**
 * Renders breadcrumb items while adding `/`
 * between each of them.
 */
export const onRenderBreadcrumbs = (items: HeaderBreadcrumbItemProps[]) => {
    const renderedItems: JSX.Element[] = [];

    items.forEach((item: HeaderBreadcrumbItemProps, index) => {
        renderedItems.push(<HeaderBreadcrumbItem key={item.text} {...item} />);

        if (index !== items.length - 1) {
            renderedItems.push(
                <LuisText data-automation-id="slash" key={`slash_${index}`} styles={breadcrumbSlashStyles}>
                    /
                </LuisText>
            );
        }
    });

    return (
        <Stack horizontal verticalAlign="center" verticalFill tokens={{ childrenGap: 10 }}>
            {renderedItems}
        </Stack>
    );
};
